// stores/auth.ts
import { getAuth, signOut, type User as FirebaseUser } from 'firebase/auth';
import { defineStore } from 'pinia';

import { userRole, type UserDto } from '@/lib/api/schemas';

import { useLeadsStore } from './useLeadsStore';
import { useUsersTableStore } from './useUsersTableStore';

import { authMe } from '@/composables/api/auth/auth';
import { AppRoutes } from '@/constants/routes';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    firebaseUser: null as FirebaseUser | null,
    user: null as UserDto | null,
    loading: true,
    initialized: false,
    loadingPromise: null as Promise<UserDto | null> | null,
  }),

  getters: {
    isAuthenticated: s => !!s.user,
    isAdmin: s => s.user?.role === userRole.Admin,
    isLeadProvider: s => s.user?.role === userRole.LeadProvider,
    isMandatary: s => s.user?.role === userRole.Mandatary,
    isViewer: s => s.user?.role === userRole.Viewer,
  },

  actions: {
    async fetchUser() {
      if (this.loadingPromise) {
        return this.loadingPromise;
      }

      this.loading = true;
      this.loadingPromise = (async() => {
        try {
          const user = await authMe();
          this.user = user;
          return user;
        } catch {
          this.user = null;
          return null;
        } finally {
          this.loading = false;
          this.initialized = true;
          this.loadingPromise = null;
        }
      })();

      return this.loadingPromise;
    },

    async bootstrap() {
      if (this.initialized && this.user) {
        return;
      }

      await this.fetchUser();
    },

    async logout(redirect?: string) {
      const auth = getAuth();
      await signOut(auth);

      const leadStore = useLeadsStore();
      const usersTableStore = useUsersTableStore();
      await $fetch(AppRoutes.AUTH_LOGOUT);
      leadStore.$reset();
      usersTableStore.$reset();
      this.$reset();
      navigateTo({
        path: AppRoutes.AUTH_LOGIN,
        query: redirect ? { redirect } : undefined,
      });
    },
  },
});
